import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import SEOHead, { SITE_URL } from '../../components/feature/SEOHead';

const team = [
  {
    name: 'Ghost_Actual',
    role: 'Founder & Editor in Chief',
    tag: 'EIC',
    bio: '10+ years covering Call of Duty. Former esports analyst for CDL teams. Knows every gun meta going back to Black Ops 2.',
    image: 'https://public.readdy.ai/ai/img_res/611393389085f635480ede5563e19ff8.jpg',
  },
  {
    name: 'ShadowOps_7',
    role: 'Senior News Writer',
    tag: 'WZ',
    bio: 'Warzone specialist and ranked Iridescent player. Covers competitive meta, weapon tuning, and tournament results.',
    image: 'https://readdy.ai/api/search-image?query=young%20gamer%20writer%20profile%20dark%20studio%20lighting%20modern%20casual%20style%20portrait%20dark%20moody%20background%20professional%20headshot&width=300&height=300&seq=team-2&orientation=squarish',
  },
  {
    name: 'DarkAether_X',
    role: 'Zombies & Esports Reporter',
    tag: 'ZM',
    bio: "Zombies Easter egg master and CDL correspondent. Attended every major CDL event since the league's inception.",
    image: 'https://readdy.ai/api/search-image?query=esports%20reporter%20journalist%20professional%20portrait%20dark%20background%20dramatic%20studio%20lighting%20gaming%20industry%20professional%20headshot&width=300&height=300&seq=team-3&orientation=squarish',
  },
  {
    name: 'CoD_Leaker99',
    role: 'Investigative Writer',
    tag: 'LEAK',
    bio: "Deep in the files before the devs patch them. If there's something to find, it's already been found.",
    image: 'https://readdy.ai/api/search-image?query=mysterious%20hacker%20data%20miner%20portrait%20dark%20moody%20background%20purple%20tinted%20dramatic%20lighting%20cyberpunk%20aesthetic%20young%20person%20profile&width=300&height=300&seq=team-4&orientation=squarish',
  },
];

const milestones = [
  { year: '2019', event: 'CallOfDutyNext.com launches — Modern Warfare day-one coverage.' },
  { year: '2020', event: 'Warzone drops — site traffic explodes 1,200% in a single month.' },
  { year: '2021', event: 'Surpass 10,000 newsletter subscribers.' },
  { year: '2023', event: 'Dedicated CDL esports coverage begins. Press credentials granted.' },
  { year: '2025', event: '2 million monthly readers, 50K+ newsletter operators.' },
  { year: '2026', event: 'Full site rebuild with real-time patch note tracking and admin system.' },
];

const stats = [
  { value: '2M+', label: 'Monthly Readers' },
  { value: '1,200+', label: 'Articles' },
  { value: '50K+', label: 'Subscribers' },
  { value: '7 YRS', label: 'of Coverage' },
];

const aboutSchema = [
  {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: 'About CallOfDutyNext.com — The #1 CoD News Site',
    description:
      'Learn about CallOfDutyNext.com — built by CoD fans for CoD fans. Our team of writers, analysts, and insiders have been covering Call of Duty since 2019.',
    url: `${SITE_URL}/about`,
    isPartOf: { '@type': 'WebSite', name: 'CallOfDutyNext.com', url: SITE_URL },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'CallOfDutyNext.com',
    url: SITE_URL,
    foundingDate: '2019',
    description:
      'The internet\'s most dedicated Call of Duty coverage — news, patch notes, Warzone updates, and CDL esports since 2019.',
    sameAs: ['https://twitter.com/CallOfDutyNext'],
    numberOfEmployees: { '@type': 'QuantitativeValue', value: 4 },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
      { '@type': 'ListItem', position: 2, name: 'About', item: `${SITE_URL}/about` },
    ],
  },
];

function Tag({ label }: { label: string }) {
  return (
    <span className="inline-block font-['Barlow_Condensed'] font-bold text-xs tracking-wider text-white bg-[#2a2a2a] border border-[#3a3a3a] px-2 py-0.5">
      {label}
    </span>
  );
}

export default function AboutPage() {
  return (
    <div className="bg-[#111] min-h-screen">
      <SEOHead
        title="About CallOfDutyNext.com — Built by CoD Fans, For CoD Fans"
        description="CallOfDutyNext.com has covered Call of Duty since 2019. Meet the team behind the #1 CoD news site — 2M+ monthly readers, 50K+ newsletter subscribers, 7 years of coverage."
        keywords="about CallOfDutyNext, CoD news team, Call of Duty writers, CoD journalists, esports analysts"
        canonical="/about"
        schema={aboutSchema}
      />
      <Navbar />

      <div className="pt-14">
        {/* ── Title bar ─────────────────────────────── */}
        <div className="border-b border-[#1e1e1e] bg-[#0d0d0d]">
          <div className="max-w-7xl mx-auto px-6 py-5">
            <h1 className="font-['Barlow_Condensed'] font-black text-white text-3xl uppercase tracking-wider leading-none">
              About
            </h1>
            <p className="text-[#555] text-sm mt-1">
              Built by CoD fans, for CoD fans. The most dedicated Call of Duty coverage on the internet.
            </p>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-6 py-10">

          {/* ── Mission two-col ────────────────────── */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 border-b border-[#1e1e1e] pb-12 mb-12">
            {/* Left text */}
            <div className="lg:col-span-2">
              <div className="flex items-center gap-3 mb-5">
                <Tag label="MISSION" />
                <Tag label="2019–2026" />
              </div>
              <h2 className="font-['Barlow_Condensed'] font-black text-white text-5xl uppercase leading-tight mb-6">
                Your #1 Source<br />For CoD Intel
              </h2>
              <p className="text-[#888] text-base leading-relaxed mb-4">
                CallOfDutyNext.com was born from a simple obsession: covering Call of Duty better than anyone else. We started as a small community blog in 2019 and grew into the internet&apos;s most trusted CoD news source.
              </p>
              <p className="text-[#888] text-base leading-relaxed mb-8">
                We don&apos;t just report the news — we analyze it. Every patch note is broken down. Every weapon nerf is tested. Every esports result is contextualized. This is the home for players who want depth, not just headlines.
              </p>
              {/* Stats row */}
              <div className="grid grid-cols-4 gap-0 border border-[#1e1e1e]">
                {stats.map((s, i) => (
                  <div
                    key={s.label}
                    className={`py-5 text-center ${i < stats.length - 1 ? 'border-r border-[#1e1e1e]' : ''}`}
                  >
                    <div className="font-['Barlow_Condensed'] font-black text-[#00ff41] text-3xl leading-none">{s.value}</div>
                    <div className="text-[#555] text-xs tracking-wider uppercase mt-1">{s.label}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right image */}
            <div className="lg:col-span-1">
              <div className="overflow-hidden w-full h-64 lg:h-full" style={{ minHeight: '280px' }}>
                <img
                  src="https://readdy.ai/api/search-image?query=Call%20of%20Duty%20gaming%20setup%20dark%20room%20multiple%20monitors%20showing%20game%20footage%20dramatic%20moody%20lighting%20atmospheric%20gaming%20station%20premium%20setup%20dark%20themed%20editorial%20style&width=600&height=700&seq=cod-about-img-2&orientation=portrait"
                  alt="Editorial Setup"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>

          {/* ── Team ──────────────────────────────── */}
          <div className="border-b border-[#1e1e1e] pb-12 mb-12">
            <div className="flex items-center gap-3 mb-8">
              <h2 className="font-['Barlow_Condensed'] font-black text-white text-2xl uppercase tracking-widest">
                The Squad
              </h2>
              <div className="flex-1 h-px bg-[#1e1e1e]" />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-0 border border-[#1e1e1e]">
              {team.map((member, i) => (
                <div
                  key={member.name}
                  className={`group ${i < team.length - 1 ? 'border-r border-[#1e1e1e]' : ''}`}
                >
                  <div className="overflow-hidden w-full" style={{ height: '220px' }}>
                    <img
                      src={member.image}
                      alt={member.name}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-5 border-t border-[#1e1e1e]">
                    <div className="flex items-center justify-between mb-1">
                      <div className="font-['Barlow_Condensed'] font-black text-white text-lg uppercase">
                        {member.name}
                      </div>
                      <Tag label={member.tag} />
                    </div>
                    <div className="text-[#555] text-xs font-['Barlow_Condensed'] font-bold tracking-widest uppercase mb-3">
                      {member.role}
                    </div>
                    <p className="text-[#777] text-sm leading-relaxed">{member.bio}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* ── Timeline ─────────────────────────── */}
          <div className="border-b border-[#1e1e1e] pb-12 mb-12">
            <div className="flex items-center gap-3 mb-8">
              <h2 className="font-['Barlow_Condensed'] font-black text-white text-2xl uppercase tracking-widest">
                Our Journey
              </h2>
              <div className="flex-1 h-px bg-[#1e1e1e]" />
            </div>

            <div className="space-y-0 border border-[#1e1e1e]">
              {milestones.map((m, i) => (
                <div
                  key={m.year}
                  className={`flex items-stretch ${i < milestones.length - 1 ? 'border-b border-[#1e1e1e]' : ''}`}
                >
                  <div className="w-24 shrink-0 flex items-center justify-center border-r border-[#1e1e1e] py-5">
                    <span className="font-['Barlow_Condensed'] font-black text-[#00ff41] text-xl">{m.year}</span>
                  </div>
                  <div className="flex-1 flex items-center px-6 py-5">
                    <p className="text-[#888] text-sm leading-relaxed">{m.event}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* ── CTA ───────────────────────────────── */}
          <div className="border border-[#1e1e1e] bg-[#0d0d0d]">
            <div className="grid grid-cols-1 lg:grid-cols-3">
              <div className="lg:col-span-2 p-10 border-r-0 lg:border-r border-[#1e1e1e]">
                <Tag label="NEWSLETTER" />
                <h3 className="font-['Barlow_Condensed'] font-black text-white text-4xl uppercase leading-tight mt-4 mb-3">
                  Want the Best<br />CoD Coverage?
                </h3>
                <p className="text-[#777] text-sm leading-relaxed max-w-lg">
                  Subscribe to our newsletter and never miss a beat. Join 50K+ operators already in the loop — free, no spam.
                </p>
              </div>
              <div className="p-10 flex items-center justify-center">
                <Link
                  to="/newsletter"
                  className="font-['Barlow_Condensed'] font-black text-sm tracking-widest bg-[#00ff41] text-black px-8 py-4 hover:bg-[#00e63a] transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2"
                >
                  SUBSCRIBE NOW
                  <i className="ri-arrow-right-line" />
                </Link>
              </div>
            </div>
          </div>

        </div>
      </div>

      <Footer />
    </div>
  );
}
